import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputMaskModule } from 'primeng/inputmask';

import { SkeletonModule } from 'primeng/skeleton';
import { DrawerModule } from 'primeng/drawer';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EfetivoService,
  FuncaoService,
  CategoriaService,
  TurnoService,
} from '@core/services/domain-services';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { forkJoin } from 'rxjs';
import { EnumService } from '@shared/api/services/enum.service';
import {
  GeneroEnum,
  EstadoEfetivoEnum,
  ParentescoEnum,
} from '@core/models/models';

@Component({
  selector: 'app-editar-efetivo',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    Select,
    DatePicker,
    InputNumberModule,
    InputMaskModule,

    SkeletonModule,
    DrawerModule,
    CommonModule,
  ],

  templateUrl: './editar-efetivo.component.html',
  styleUrl: './editar-efetivo.component.scss',
})
export class EditarEfetivoComponent implements OnInit, OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly msg = inject(MessageService);
  private readonly efetivoService = inject(EfetivoService);
  private readonly funcaoService = inject(FuncaoService);
  private readonly categoriaService = inject(CategoriaService);
  private readonly turnoService = inject(TurnoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly enumService = inject(EnumService);

  @Input() visible = false;
  @Input() efetivoId: number | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  @ViewChild('cameraVideo') cameraVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('cameraCanvas') cameraCanvas!: ElementRef<HTMLCanvasElement>;

  loading = false;
  loadingEfetivo = false;
  loadingDropdowns = false;
  ibanMask: string = '';
  private submitted = false;

  // Foto
  selectedFoto: File | null = null;
  fotoPreviewUrl: string | null = null;

  // Câmara
  cameraOpen = false;
  capturedPhotoUrl: string | null = null;
  private mediaStream: MediaStream | null = null;

  form!: FormGroup;
  funcoes: any[] = [];
  categorias: any[] = [];
  turnos: { label: string; value: any }[] = [];
  parentescos = [
    { label: 'Cônjuge', value: ParentescoEnum.CONJUGE },
    { label: 'Progenitor', value: ParentescoEnum.PROGENITOR },
    { label: 'Irmão/Irmã', value: ParentescoEnum.IRMAO },
    { label: 'Filho/Filha', value: ParentescoEnum.FILHO },
    { label: 'Outro', value: ParentescoEnum.OUTRO },
  ];

  generos = [
    { label: 'Masculino', value: GeneroEnum.MASCULINO },
    { label: 'Feminino', value: GeneroEnum.FEMININO },
  ];

  estados = [
    { label: 'Ativo', value: EstadoEfetivoEnum.ATIVO },
    { label: 'Inativo', value: EstadoEfetivoEnum.INATIVO },
    { label: 'Afastado', value: EstadoEfetivoEnum.AFASTADO },
    { label: 'Suspenso', value: EstadoEfetivoEnum.SUSPENSO },
    { label: 'De Folga', value: EstadoEfetivoEnum.DE_FOLGA },
    { label: 'Férias', value: EstadoEfetivoEnum.FERIAS },
  ];

  angolanBanks = [
    { label: 'BAI - Banco Angolano de Investimentos', value: 'BAI' },
    { label: 'BFA - Banco de Fomento Angola', value: 'BFA' },
    { label: 'BPC - Banco de Poupança e Crédito', value: 'BPC' },
    { label: 'BIC - Banco BIC', value: 'BIC' },
    { label: 'BCI - Banco de Comércio e Indústria', value: 'BCI' },
    { label: 'BCGA - Banco Caixa Geral Angola', value: 'BCGA' },
    { label: 'BCA - Banco Comercial Angolano', value: 'BCA' },
    { label: 'BDA - Banco de Desenvolvimento de Angola', value: 'BDA' },
    { label: 'BMA - Banco Millennium Atlântico', value: 'BMA' },
    { label: 'BSOL - Banco Sol', value: 'BSOL' },
    { label: 'YETU - Banco YETU', value: 'YETU' },
    { label: 'BANC - Banco Angolano de Negócios e Comércio', value: 'BANC' },
    { label: 'BMF - Banco BAI Microfinanças', value: 'BMF' },
    { label: 'BCH - Banco Comercial do Huambo', value: 'BCH' },
    { label: 'SBA - Standard Bank Angola', value: 'SBA' },
  ];

  private readonly mockTurnos = [
    { label: 'Diurno (06h–18h)', value: 'DIURNO' },
    { label: 'Noturno (18h–06h)', value: 'NOTURNO' },
    { label: 'Integral (24 horas)', value: '24_HORAS' },
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['efetivoId'] || changes['visible']) &&
      this.visible &&
      this.efetivoId
    ) {
      this.resetState();
      this.loadEfetivo();
    }
  }

  ngOnDestroy(): void {
    this.stopStream();
  }

  initForm(): void {
    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      numBI: ['', Validators.required],
      dataNascimento: [
        '',
        [Validators.required, this.dataNaoFutura.bind(this)],
      ],
      genero: [null, Validators.required],
      numTelefone: [
        '',
        [Validators.required, Validators.pattern(/^9[0-9]{8}$/)],
      ],
      email: ['', Validators.email],
      modara: [''],
      codigoMec: [''],
      mac: [''],
      iban: [{ value: null, disabled: true }],
      banco: [null],
      turno: [null],
      salario: [null],
      idFuncao: [null, Validators.required],
      idCategoria: [null, Validators.required],
      dataAdmissao: ['', [Validators.required, this.dataNaoFutura.bind(this)]],
      estado: ['Ativo'],
      nomeContactoEmergencia: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/),
        ],
      ],
      numTelContactoEmergencia: ['', [Validators.pattern(/^9[0-9]{8}$/)]],
      parentescoContactoEmergencia: [''],
    });
  }

  private resetState(): void {
    this.form?.reset({ estado: 'Ativo' });
    this.selectedFoto = null;
    this.fotoPreviewUrl = null;
    this.submitted = false;
    this.loading = false;
    this.closeCamera();
  }

  closeDrawer(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  getBankPrefix(bank: string): string {
    const bankCodeMap: Record<string, string> = {
      BAI: '0040',
      BFA: '0006',
      BPC: '0010',
      BIC: '0051',
      BCI: '0005',
      BCGA: '0004',
      BCA: '0043',
      BDA: '0054',
      BMA: '0055',
      BSOL: '0044',
      YETU: '0066',
      BANC: '0053',
      BMF: '0048',
      BCH: '0059',
      SBA: '0060',
    };
    return bankCodeMap[bank] || '';
  }

  onBancoChange(event: any): void {
    const bank = event?.value;
    const prefix = this.getBankPrefix(bank);
    this.ibanMask = prefix ? `AO06 ${prefix} 9999 9999 9999 9999 9` : '';
    const ibanControl = this.form.get('iban');
    if (prefix) {
      ibanControl?.enable();
    } else {
      ibanControl?.disable();
    }
    this.form.patchValue({
      iban: null,
    });
  }

  loadDropdowns(): void {
    this.loadingDropdowns = true;
    forkJoin({
      funcoes: this.funcaoService.getAll({ pageSize: 1000 }),
      categorias: this.categoriaService.getAll({ pageSize: 1000 }),
      turnos: this.turnoService.getAll({ pageSize: 100 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.funcoes = (res.funcoes.data || []).map((f: any) => ({
            id: f.id,
            nome: f.designacao,
          }));
          this.categorias = (res.categorias.data || []).map((c: any) => ({
            id: c.id,
            nome: c.designacao,
          }));
          this.turnos = res.turnos.data?.length
            ? res.turnos.data.map((t: any) => ({
                label: t.designacao,
                value: t.id,
              }))
            : this.mockTurnos;

          this.loadingDropdowns = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.turnos = this.mockTurnos;
          this.loadingDropdowns = false;
        },
      });
  }

  loadEfetivo(): void {
    if (!this.efetivoId) return;
    this.loadingEfetivo = true;
    this.efetivoService
      .getById(this.efetivoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const ef = res?.data || res;
          let generoVal = ef.genero;
          if (generoVal === 'Masculino' || generoVal === 'MASCULINO') {
            generoVal = GeneroEnum.MASCULINO;
          } else if (generoVal === 'Feminino' || generoVal === 'FEMININO') {
            generoVal = GeneroEnum.FEMININO;
          } else if (generoVal !== null && generoVal !== undefined) {
            generoVal = Number(generoVal);
          }

          let estadoVal = ef.estadoEfetivo;
          if (estadoVal !== null && estadoVal !== undefined) {
            estadoVal = Number(estadoVal);
          } else {
            estadoVal = EstadoEfetivoEnum.ATIVO;
          }

          let parentescoVal = ef.efetivoGrauParentescoEnum;
          if (parentescoVal === 'CONJUGE' || parentescoVal === 'Conjuge') {
            parentescoVal = ParentescoEnum.CONJUGE;
          } else if (
            parentescoVal === 'PROGENITOR' ||
            parentescoVal === 'Progenitor' ||
            parentescoVal === 'PAI' ||
            parentescoVal === 'Pai' ||
            parentescoVal === 'MAE' ||
            parentescoVal === 'Mãe'
          ) {
            parentescoVal = ParentescoEnum.PROGENITOR;
          } else if (
            parentescoVal === 'IRMAO' ||
            parentescoVal === 'Irmão' ||
            parentescoVal === 'Irmã'
          ) {
            parentescoVal = ParentescoEnum.IRMAO;
          } else if (parentescoVal === 'FILHO' || parentescoVal === 'Filho') {
            parentescoVal = ParentescoEnum.FILHO;
          } else if (parentescoVal === 'OUTRO' || parentescoVal === 'Outro') {
            parentescoVal = ParentescoEnum.OUTRO;
          } else if (parentescoVal !== null && parentescoVal !== undefined) {
            parentescoVal = Number(parentescoVal);
          } else {
            const legacyVal = ef.parentescoContactoEmergencia;
            if (legacyVal) {
              const legacyMap: Record<string, number> = {
                CONJUGE: 1,
                Conjuge: 1,
                PROGENITOR: 2,
                Progenitor: 2,
                PAI: 2,
                Pai: 2,
                IRMAO: 3,
                Irmão: 3,
                Irmao: 3,
                FILHO: 4,
                Filho: 4,
                OUTRO: 5,
                Outro: 5,
              };
              parentescoVal = legacyMap[legacyVal] ?? ParentescoEnum.OUTRO;
            } else {
              parentescoVal = ParentescoEnum.OUTRO;
            }
          }

          let loadedIban = ef.iban || '';
          if (loadedIban) {
            loadedIban = loadedIban.replace(/\s/g, '');
            if (loadedIban.length === 21 && !loadedIban.startsWith('AO06')) {
              loadedIban = 'AO06' + loadedIban;
            }
          }

          const bankPrefix = this.getBankPrefix(ef.banco);
          this.ibanMask = bankPrefix
            ? `AO06 ${bankPrefix} 9999 9999 9999 9999 9`
            : '';

          this.form.patchValue({
            nome: ef.nome,
            numBI: ef.numBI,
            dataNascimento: ef.dataNascimento
              ? new Date(ef.dataNascimento)
              : null,
            genero: generoVal,
            numTelefone: ef.numTelefone,
            email: ef.email,
            modara: ef.modara,
            codigoMec: ef.codigoMec || '',
            mac: ef.mac || ef.Mac || '',
            iban: loadedIban || null,
            banco: ef.banco,
            turno: ef.turno || null,
            salario: ef.salario || null,
            dataAdmissao: ef.dataAdmissao ? new Date(ef.dataAdmissao) : null,
            idFuncao: ef.idFuncao || ef.funcao?.id,
            idCategoria: ef.idCategoria || ef.categoria?.id,
            nomeContactoEmergencia: ef.nomeContactoEmergencia,
            numTelContactoEmergencia: ef.numTelContactoEmergencia,
            parentescoContactoEmergencia: parentescoVal,
            estado: estadoVal,
          });
          if (bankPrefix) {
            this.form.get('iban')?.enable();
          } else {
            this.form.get('iban')?.disable();
          }
          this.fotoPreviewUrl = ef.urlFotografia || null;
          this.loadingEfetivo = false;
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          const detail =
            err?.error?.detail ||
            err?.error?.message ||
            'Efetivo não encontrado.';
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: detail,
            life: 3000,
          });
          this.loadingEfetivo = false;
          this.closeDrawer();
          this.cdr.detectChanges();
        },
      });
  }

  // ── Foto ────────────────────────────────────────────────
  async onFotoSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 5 * 1024 * 1024) {
      this.msg.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Foto muito grande. Máximo 5MB.',
      });
      input.value = '';
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      this.msg.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Formato inválido. Use JPG, PNG ou WEBP.',
      });
      input.value = '';
      return;
    }
    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    const reader = new FileReader();
    reader.onload = () => {
      this.fotoPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(compressedFile);
  }

  removeFoto(): void {
    this.selectedFoto = null;
    // Mantém fotoPreviewUrl com a imagem actual da API
  }

  // ── Câmara ───────────────────────────────────────────────
  openCamera(): void {
    this.cameraOpen = true;
    this.capturedPhotoUrl = null;
    setTimeout(() => this.startStream(), 100);
  }

  private startStream(): void {
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
        audio: false,
      })
      .then((stream) => {
        this.mediaStream = stream;
        const video = this.cameraVideo?.nativeElement;
        if (video) {
          video.srcObject = stream;
          video.play();
        }
      })
      .catch(() => {
        this.msg.add({
          severity: 'error',
          summary: 'Câmara indisponível',
          detail:
            'Não foi possível aceder à câmara. Verifique as permissões do browser.',
          life: 5000,
        });
        this.cameraOpen = false;
      });
  }

  capturePhoto(): void {
    const video = this.cameraVideo?.nativeElement;
    const canvas = this.cameraCanvas?.nativeElement;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 640;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    this.capturedPhotoUrl = canvas.toDataURL('image/jpeg', 0.9);
    this.stopStream();
  }

  retakePhoto(): void {
    this.capturedPhotoUrl = null;
    setTimeout(() => this.startStream(), 100);
  }

  async confirmPhoto(): Promise<void> {
    if (!this.capturedPhotoUrl) return;
    const arr = this.capturedPhotoUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const file = new File([u8arr], `foto-camera-${Date.now()}.jpg`, {
      type: mime,
    });
    if (file.size > 5 * 1024 * 1024) {
      this.msg.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Foto muito grande. Tente novamente.',
        life: 3000,
      });
      return;
    }
    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    const reader = new FileReader();
    reader.onload = () => {
      this.fotoPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(compressedFile);
    this.closeCamera();
  }

  closeCamera(): void {
    this.stopStream();
    this.cameraOpen = false;
    this.capturedPhotoUrl = null;
  }

  private stopStream(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
  }

  // ── Submit ───────────────────────────────────────────────
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.msg.add({
        severity: 'error',
        summary: 'Erro de Validação',
        detail: 'Preencha todos os campos obrigatórios.',
        life: 4000,
      });
      return;
    }
    if (this.submitted || this.loading) return;
    this.submitted = true;
    this.loading = true;

    const v = this.form.value;
    const ibanNormalizado = (v.iban || '').replace(/\s/g, '');
    const payload = {
      nome: v.nome,
      numBI: v.numBI,
      dataNascimento: v.dataNascimento
        ? this.formatDate(v.dataNascimento)
        : null,
      genero: v.genero,
      numTelefone: v.numTelefone,
      email: v.email || '',
      modara: v.modara || '',
      CodigoMec: v.codigoMec ? v.codigoMec.trim() : null,
      mac: v.mac ? v.mac.trim() : null,
      Mac: v.mac ? v.mac.trim() : null,
      iban: ibanNormalizado,
      banco: v.banco || '',
      turno: v.turno || null,
      salario: v.salario || null,
      idFuncao: v.idFuncao,
      idCategoria: v.idCategoria,
      dataAdmissao: v.dataAdmissao ? this.formatDate(v.dataAdmissao) : null,
      estadoEfetivo:
        v.estado != null ? Number(v.estado) : EstadoEfetivoEnum.ATIVO,
      nomeContactoEmergencia: v.nomeContactoEmergencia || '',
      numTelContactoEmergencia: v.numTelContactoEmergencia || '',
      parentescoContactoEmergencia: v.parentescoContactoEmergencia
        ? String(v.parentescoContactoEmergencia)
        : '',
      efetivoGrauParentescoEnum: v.parentescoContactoEmergencia
        ? Number(v.parentescoContactoEmergencia)
        : ParentescoEnum.OUTRO,
    };

    this.efetivoService
      .update(this.efetivoId!, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.selectedFoto) {
            this.uploadFotoEGuardar();
          } else {
            this.onSaveSuccess();
          }
        },
        error: (err: any) => {
          this.loading = false;
          this.submitted = false;
          let detail = 'Falha ao actualizar.';
          if (err?.error?.errors) {
            detail =
              Object.values(err.error.errors).flat().join('; ') || detail;
          } else if (err?.error?.message) {
            detail = err.error.message;
          }
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail,
            life: 5000,
          });
          this.cdr.detectChanges();
        },
      });
  }

  private uploadFotoEGuardar(): void {
    this.efetivoService
      .getTipoAnexo('Fotografia')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (idTipoAnexo: any) => {
          this.efetivoService
            .uploadFoto(
              this.efetivoId!,
              this.selectedFoto!,
              idTipoAnexo,
              'Fotografia',
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.onSaveSuccess(),
              error: () => {
                this.msg.add({
                  severity: 'warn',
                  summary: 'Aviso',
                  detail: 'Dados guardados, mas falha ao actualizar a foto.',
                  life: 4000,
                });
                this.onSaveSuccess();
              },
            });
        },
        error: () => this.onSaveSuccess(),
      });
  }

  private onSaveSuccess(): void {
    this.msg.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Efetivo actualizado com sucesso.',
    });
    this.loading = false;
    this.submitted = false;
    this.cdr.detectChanges();
    this.saved.emit();
    setTimeout(() => this.closeDrawer(), 800);
  }

  private formatDate(date: any): string {
    if (!date) return '';
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(date)) return date.slice(0, 10);
      date = new Date(date);
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  private dataNaoFutura(control: any) {
    const val = control.value;
    if (!val) return null;
    const data = new Date(val);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return data > hoje ? { dataFutura: true } : null;
  }

  private parseEnumResponse(
    response: any,
    options?: {
      useKeyAsValue?: boolean;
      labelMap?: Record<string, string>;
      valueMap?: Record<string, string>;
    },
  ): { label: string; value: string }[] {
    const data = response?.data || {};
    return Object.keys(data).map((key) => {
      const rawValue = data[key];
      let value = options?.useKeyAsValue ? key : rawValue;
      if (options?.valueMap) value = options.valueMap[key] ?? value;
      const label = options?.labelMap
        ? (options.labelMap[key] ?? rawValue)
        : rawValue;
      return { label, value };
    });
  }
}
